import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, Clock, CheckCircle, ChefHat, ChevronRight, X, CreditCard, 
  QrCode, Copy, Calendar, MapPin, Receipt, RefreshCcw, Truck, XCircle, AlertTriangle, Flag, Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

import { useCart } from '@/context/CartContext';

import { API_URL } from '@/config/api';

// POS-grade status config
const statusConfig = {
  created: { icon: Clock, label: 'Created', color: 'bg-slate-100 text-slate-700', accent: 'border-l-slate-400', step: 0 },
  payment_pending: { icon: CreditCard, label: 'Awaiting Payment', color: 'bg-yellow-100 text-yellow-700', accent: 'border-l-yellow-500', step: 1 },
  paid: { icon: CheckCircle, label: 'Paid', color: 'bg-blue-100 text-blue-700', accent: 'border-l-blue-500', step: 2 },
  preparing: { icon: ChefHat, label: 'Preparing', color: 'bg-purple-100 text-purple-700', accent: 'border-l-purple-500', step: 3 },
  ready_for_collection: { icon: Package, label: 'Ready for Collection', color: 'bg-green-100 text-green-700', accent: 'border-l-green-500', step: 4 },
  collected: { icon: Truck, label: 'Collected', color: 'bg-emerald-600 text-white', accent: 'border-l-emerald-600', step: 5 },
  cancelled: { icon: X, label: 'Cancelled', color: 'bg-red-100 text-red-700', accent: 'border-l-red-500', step: -1 }
};

const statusSteps = ['created', 'payment_pending', 'paid', 'preparing', 'ready_for_collection', 'collected']; // eslint-disable-line no-unused-vars

export default function AppOrders() {
  const { token, isAuthenticated } = useAuth();
  const { addMultipleItems, openCart } = useCart();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [showDispute, setShowDispute] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeCategory, setDisputeCategory] = useState('quality');
  const [submittingDispute, setSubmittingDispute] = useState(false);

  const fetchOrders = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/orders`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Sort by date descending
        data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setOrders(data);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
      // Auto-refresh every 30 seconds for active orders
      const interval = setInterval(fetchOrders, 30000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, fetchOrders]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchOrders();
  };

  const copyCollectionCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Collection code copied!');
  };

  // Check if order can be cancelled (within 24 hours, not preparing/completed)
  const canCancelOrder = (order) => {
    const nonCancellableStatuses = ['cancelled', 'preparing', 'ready_for_collection', 'collected', 'delivered'];
    if (nonCancellableStatuses.includes(order.status)) return false;
    
    const createdAt = new Date(order.created_at);
    const hoursSinceCreation = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60);
    return hoursSinceCreation <= 24;
  };

  // Check if order can be deleted (unpaid only)
  const canDeleteOrder = (order) => {
    const nonDeletableStatuses = ['preparing', 'ready_for_collection', 'collected', 'delivered'];
    if (nonDeletableStatuses.includes(order.status)) return false;
    return order.payment_status !== 'paid';
  };

  // Get time remaining to cancel
  const getCancelTimeRemaining = (order) => {
    const createdAt = new Date(order.created_at);
    const deadline = new Date(createdAt.getTime() + 24 * 60 * 60 * 1000);
    const remaining = deadline.getTime() - Date.now();
    
    if (remaining <= 0) return null;
    
    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const handleCancelOrder = async () => {
    if (!selectedOrder) return;
    
    setCancelling(true);
    try {
      const response = await fetch(`${API_URL}/api/orders/${selectedOrder.order_id}/cancel`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Order cancelled successfully');
        if (data.reward_restored) {
          toast.success('Your tier reward is back — it will apply automatically to your next order! 🎁', { duration: 7000 });
        }
        if (data.refund_message) {
          toast.info(data.refund_message, { duration: 6000 });
        }
        setShowCancelConfirm(false);
        setSelectedOrder(null);
        fetchOrders();
      } else {
        toast.error(data.detail || 'Failed to cancel order');
      }
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.error('Failed to cancel order');
    } finally {
      setCancelling(false);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!confirm('Are you sure you want to delete this unpaid order? This cannot be undone.')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/orders/${orderId}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Order deleted successfully');
        if (data.reward_restored) {
          toast.success('Your tier reward is back — it will apply automatically to your next order! 🎁', { duration: 7000 });
        }
        setSelectedOrder(null);
        fetchOrders();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete order');
      }
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error('Failed to delete order');
    }
  };

  const handleReorder = async (orderId) => {
    setReordering(true);
    try {
      const response = await fetch(`${API_URL}/api/orders/${orderId}/reorder`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to reorder');
      }
      const data = await response.json();
      
      if (data.items.length === 0) {
        toast.error('None of the items from this order are currently available');
        return;
      }
      
      addMultipleItems(data.items);
      
      if (data.unavailable_items?.length > 0) {
        toast.info(`Added ${data.items.length} items. Unavailable: ${data.unavailable_items.join(', ')}`);
      } else {
        toast.success(`${data.items.length} item${data.items.length > 1 ? 's' : ''} added to your cart!`);
      }
      
      setSelectedOrder(null);
      openCart();
    } catch (error) {
      toast.error(error.message || 'Failed to reorder');
    } finally {
      setReordering(false);
    }
  };

  const handleRaiseDispute = async () => {
    if (!disputeReason.trim()) { toast.error('Please describe the issue'); return; }
    setSubmittingDispute(true);
    try {
      const response = await fetch(`${API_URL}/api/orders/${selectedOrder.order_id}/dispute`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: disputeReason.trim(), category: disputeCategory })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to submit');
      toast.success(data.message);
      setShowDispute(false);
      setDisputeReason('');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmittingDispute(false);
    }
  };

  const getStatusInfo = (status) => statusConfig[status] || statusConfig.created;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { 
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Redirect to login if not authenticated
  if (!isAuthenticated && !loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-background flex flex-col items-center justify-center p-5"
      >
        <Package className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-800 mb-2">Sign in to view orders</h2>
        <p className="text-slate-500 mb-6 text-center">Track your order status and collection codes</p>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/auth')}
          className="bg-primary text-white px-8 py-3 rounded-xl font-medium"
        >
          Sign In
        </motion.button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-background"
      data-testid="app-orders"
    >
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-lg border-b border-border">
        <div className="px-5 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-secondary-900">My Orders</h1>
            <p className="text-sm text-muted-foreground">{orders.length} order{orders.length !== 1 ? 's' : ''}</p>
          </div>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-3 bg-primary/10 rounded-xl"
          >
            <RefreshCcw className={`h-5 w-5 text-primary ${refreshing ? 'animate-spin' : ''}`} />
          </motion.button>
        </div>
      </div>

      {/* Orders List */}
      <div className="px-5 py-4 pb-24">
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-36 bg-muted rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : orders.length > 0 ? (
          <div className="space-y-4">
            {orders.map((order, idx) => {
              const statusInfo = getStatusInfo(order.status);
              const StatusIcon = statusInfo.icon;
              const needsPayment = order.status === 'payment_pending' || order.status === 'created';
              const hasCollectionCode = order.collection_code && ['paid', 'preparing', 'ready_for_collection'].includes(order.status);
              
              return (
                <motion.div
                  key={order.order_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  onClick={() => setSelectedOrder(order)}
                  className={`bg-white rounded-2xl p-4 border-l-4 ${statusInfo.accent} shadow-sm cursor-pointer active:scale-[0.98] transition-transform`}
                  data-testid={`order-${order.order_id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${statusInfo.color}`}>
                        <StatusIcon className="h-3.5 w-3.5" />
                        {statusInfo.label}
                      </span>
                      {needsPayment && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 animate-pulse">
                          Pay Now
                        </span>
                      )}
                      {hasCollectionCode && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 flex items-center gap-1">
                          <QrCode className="h-3 w-3" />
                          {order.collection_code}
                        </span>
                      )}
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Order #{order.order_id.slice(-8).toUpperCase()}</p>
                      <p className="font-semibold text-secondary-900">
                        {order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? 's' : ''} • R{order.total_amount?.toFixed(2)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                        <Calendar className="h-3 w-3" />
                        {order.delivery_date || 'N/A'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">{formatDate(order.created_at)}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20">
            <Package className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-secondary-900 mb-2">No Orders Yet</h3>
            <p className="text-muted-foreground mb-6">Start ordering delicious meals from our menu</p>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/menu')}
              className="bg-primary text-white px-8 py-3 rounded-xl font-medium"
            >
              Browse Menu
            </motion.button>
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      <AnimatePresence>
        {selectedOrder && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => { setSelectedOrder(null); setShowQR(false); }}
              className="fixed inset-0 bg-black/60 z-50"
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl max-h-[90vh] overflow-auto"
              data-testid="order-detail-modal"
            >
              <div className="sticky top-0 bg-white border-b border-border p-4 flex items-center justify-between z-10">
                <div>
                  <h2 className="font-heading text-xl font-semibold">Order Details</h2>
                  <p className="text-sm text-muted-foreground">#{selectedOrder.order_id.slice(-8).toUpperCase()}</p>
                </div>
                <button 
                  onClick={() => { setSelectedOrder(null); setShowQR(false); }}
                  className="w-10 h-10 bg-muted rounded-full flex items-center justify-center"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-5 pb-24 space-y-6">
                {/* Status Timeline */}
                <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-2xl p-4">
                  <div className="flex items-center gap-3 mb-4">
                    {(() => {
                      const statusInfo = getStatusInfo(selectedOrder.status);
                      const StatusIcon = statusInfo.icon;
                      return (
                        <>
                          <div className={`p-3 rounded-xl ${statusInfo.color}`}>
                            <StatusIcon className="h-6 w-6" />
                          </div>
                          <div>
                            <p className="font-semibold text-secondary-900">{statusInfo.label}</p>
                            <p className="text-sm text-muted-foreground">
                              {selectedOrder.status === 'collected' 
                                ? 'Order completed - thank you!' 
                                : selectedOrder.status === 'ready_for_collection'
                                ? 'Your order is ready! Show your collection code.'
                                : selectedOrder.status === 'preparing'
                                ? 'Our kitchen is preparing your order'
                                : selectedOrder.status === 'paid'
                                ? 'Payment confirmed - order in queue'
                                : selectedOrder.status === 'cancelled'
                                ? 'This order was cancelled'
                                : 'Complete payment to proceed'}
                            </p>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* Progress Steps — labeled stage timeline */}
                  {selectedOrder.status !== 'cancelled' && (
                    <div className="mt-5">
                      <div className="flex items-start justify-between">
                        {[
                          { key: 'paid', label: 'Paid', icon: CheckCircle, step: 2 },
                          { key: 'preparing', label: 'Preparing', icon: ChefHat, step: 3 },
                          { key: 'ready', label: 'Ready', icon: Package, step: 4 },
                          { key: 'collected', label: 'Collected', icon: Truck, step: 5 },
                        ].map((stage, i, arr) => {
                          const currentStep = statusConfig[selectedOrder.status]?.step ?? 0;
                          const done = currentStep > stage.step;
                          const current = currentStep === stage.step;
                          const StageIcon = stage.icon;
                          return (
                            <div key={stage.key} className="flex-1 flex flex-col items-center relative" data-testid={`timeline-${stage.key}`}>
                              {i < arr.length - 1 && (
                                <div className={`absolute top-4 left-1/2 w-full h-1 ${done ? 'bg-green-500' : 'bg-slate-200'}`} />
                              )}
                              <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${done ? 'bg-green-500 text-white' : current ? 'bg-primary text-white animate-pulse' : 'bg-slate-200 text-slate-400'}`}>
                                <StageIcon className="h-4 w-4" />
                              </div>
                              <span className={`mt-1.5 text-[10px] font-medium text-center ${done || current ? 'text-secondary-900' : 'text-slate-400'}`}>{stage.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Collection Code */}
                {selectedOrder.collection_code && ['paid', 'preparing', 'ready_for_collection'].includes(selectedOrder.status) && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-4 border border-green-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <QrCode className="h-5 w-5 text-green-600" />
                        <span className="font-medium text-green-800">Collection Code</span>
                      </div>
                      <button 
                        onClick={() => setShowQR(!showQR)}
                        className="text-sm text-green-600 hover:text-green-700 font-medium"
                      >
                        {showQR ? 'Hide QR' : 'Show QR'}
                      </button>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="flex-1 bg-white rounded-xl p-4 flex items-center justify-center">
                        <span className="text-3xl font-mono font-bold tracking-widest text-green-700">
                          {selectedOrder.collection_code}
                        </span>
                      </div>
                      <button
                        onClick={() => copyCollectionCode(selectedOrder.collection_code)}
                        className="p-3 bg-green-100 rounded-xl hover:bg-green-200 transition-colors"
                      >
                        <Copy className="h-5 w-5 text-green-700" />
                      </button>
                    </div>

                    {/* QR Code */}
                    <AnimatePresence>
                      {showQR && selectedOrder.collection_code_qr && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-4 flex justify-center">
                            <div className="bg-white p-4 rounded-xl">
                              <img 
                                src={selectedOrder.collection_code_qr} 
                                alt="Collection QR Code"
                                className="w-48 h-48"
                              />
                            </div>
                          </div>
                          <p className="text-center text-sm text-green-600 mt-2">
                            Show this QR code at collection
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {/* Delivery Info */}
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                  <MapPin className="h-5 w-5 text-slate-500" />
                  <div>
                    <p className="text-sm text-slate-500">Delivery Date</p>
                    <p className="font-medium text-slate-900">{selectedOrder.delivery_date || 'Not specified'}</p>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <h3 className="font-semibold text-secondary-900 mb-3 flex items-center gap-2">
                    <Receipt className="h-4 w-4" />
                    Order Items
                  </h3>
                  <div className="space-y-3">
                    {selectedOrder.items?.map((item, idx) => (
                      <div key={item.item_id || `item-${idx}`} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                        <div className="flex items-center gap-3">
                          {item.image_url && (
                            <img src={item.image_url} alt={item.name} className="w-12 h-12 rounded-lg object-cover" />
                          )}
                          <div>
                            <p className="font-medium text-secondary-900">{item.name}</p>
                            <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                          </div>
                        </div>
                        <p className="font-semibold">R{(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Total */}
                <div className="bg-primary/5 rounded-2xl p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-secondary-900">Total Paid</span>
                    <span className="text-2xl font-bold text-primary">R{selectedOrder.total_amount?.toFixed(2)}</span>
                  </div>
                </div>

                {/* Pay Now Button */}
                {(selectedOrder.status === 'payment_pending' || selectedOrder.status === 'created') && (
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setSelectedOrder(null);
                      navigate(`/checkout/${selectedOrder.order_id}`);
                    }}
                    className="w-full h-14 bg-primary text-white rounded-2xl font-semibold text-lg flex items-center justify-center gap-2"
                    data-testid="pay-now-btn"
                  >
                    <CreditCard className="h-5 w-5" />
                    Complete Payment
                  </motion.button>
                )}

                {/* Cancel Order Button */}
                {canCancelOrder(selectedOrder) && (
                  <div className="space-y-3">
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowCancelConfirm(true)}
                      className="w-full h-12 bg-red-50 text-red-600 border border-red-200 rounded-2xl font-medium flex items-center justify-center gap-2 hover:bg-red-100 transition-colors"
                      data-testid="cancel-order-btn"
                    >
                      <XCircle className="h-5 w-5" />
                      Cancel Order
                    </motion.button>
                    <p className="text-xs text-center text-muted-foreground">
                      You can cancel within {getCancelTimeRemaining(selectedOrder)} • 
                      {selectedOrder.payment_status === 'paid' ? ' Refund will be processed automatically' : ' No payment made yet'}
                    </p>
                  </div>
                )}

                {/* Delete Unpaid Order Button */}
                {canDeleteOrder(selectedOrder) && !canCancelOrder(selectedOrder) && (
                  <div className="space-y-3">
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleDeleteOrder(selectedOrder.order_id)}
                      className="w-full h-12 bg-red-50 text-red-600 border border-red-200 rounded-2xl font-medium flex items-center justify-center gap-2 hover:bg-red-100 transition-colors"
                      data-testid="delete-order-btn"
                    >
                      <XCircle className="h-5 w-5" />
                      Delete Order
                    </motion.button>
                    <p className="text-xs text-center text-muted-foreground">
                      This unpaid order can be deleted permanently
                    </p>
                  </div>
                )}

                {/* Report an issue (paid orders) */}
                {selectedOrder.payment_status === 'paid' && selectedOrder.status !== 'cancelled' && (
                  <button
                    onClick={() => { setShowDispute(true); setDisputeReason(''); setDisputeCategory('quality'); }}
                    className="w-full h-11 mt-2 text-sm text-slate-600 border border-slate-200 rounded-2xl font-medium flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors"
                    data-testid="report-issue-btn"
                  >
                    <Flag className="h-4 w-4" /> Report an issue with this order
                  </button>
                )}

                {/* Dispute Modal */}
                <AnimatePresence>
                  {showDispute && (
                    <motion.div
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4"
                      onClick={() => setShowDispute(false)}
                    >
                      <motion.div
                        initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white rounded-2xl p-6 max-w-sm w-full"
                        data-testid="dispute-modal"
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-3 bg-orange-100 rounded-full"><Flag className="h-5 w-5 text-orange-600" /></div>
                          <div>
                            <h3 className="font-semibold text-lg">Report an issue</h3>
                            <p className="text-sm text-muted-foreground">Order #{selectedOrder.order_id.slice(-8).toUpperCase()}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          {[
                            { k: 'quality', l: 'Food quality' },
                            { k: 'missing_item', l: 'Missing item' },
                            { k: 'wrong_order', l: 'Wrong order' },
                            { k: 'other', l: 'Other' },
                          ].map(c => (
                            <button key={c.k} onClick={() => setDisputeCategory(c.k)}
                              className={`py-2 rounded-xl text-xs font-medium ${disputeCategory === c.k ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700'}`}
                              data-testid={`dispute-cat-${c.k}`}>{c.l}</button>
                          ))}
                        </div>
                        <textarea
                          value={disputeReason}
                          onChange={(e) => setDisputeReason(e.target.value)}
                          placeholder="Tell us what went wrong…"
                          rows={3}
                          className="w-full p-3 bg-slate-50 rounded-xl text-sm border-0 focus:ring-2 focus:ring-primary/30 resize-none mb-4"
                          data-testid="dispute-reason-input"
                        />
                        <div className="flex gap-3">
                          <button onClick={() => setShowDispute(false)} className="flex-1 h-12 bg-slate-100 text-slate-700 rounded-xl font-medium">Cancel</button>
                          <button onClick={handleRaiseDispute} disabled={submittingDispute}
                            className="flex-1 h-12 bg-primary text-white rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                            data-testid="dispute-submit-btn">
                            {submittingDispute ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit'}
                          </button>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Cancel Confirmation Modal */}
                <AnimatePresence>
                  {showCancelConfirm && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4"
                      onClick={() => setShowCancelConfirm(false)}
                    >
                      <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white rounded-2xl p-6 max-w-sm w-full"
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-3 bg-red-100 rounded-full">
                            <AlertTriangle className="h-6 w-6 text-red-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">Cancel Order?</h3>
                            <p className="text-sm text-muted-foreground">This action cannot be undone</p>
                          </div>
                        </div>
                        
                        <div className="bg-slate-50 rounded-xl p-4 mb-4 text-sm space-y-2">
                          <p className="font-medium">Cancellation Policy:</p>
                          <ul className="text-muted-foreground space-y-1">
                            <li>• Orders can be cancelled within 24 hours</li>
                            {selectedOrder.payment_status === 'paid' && (
                              <li>• Refund of R{selectedOrder.total_amount?.toFixed(2)} will be processed</li>
                            )}
                            <li>• Refunds take 5-10 business days</li>
                          </ul>
                        </div>

                        <div className="flex gap-3">
                          <button
                            onClick={() => setShowCancelConfirm(false)}
                            className="flex-1 h-12 bg-slate-100 text-slate-700 rounded-xl font-medium"
                            disabled={cancelling}
                          >
                            Keep Order
                          </button>
                          <button
                            onClick={handleCancelOrder}
                            disabled={cancelling}
                            className="flex-1 h-12 bg-red-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            {cancelling ? (
                              <>
                                <RefreshCcw className="h-4 w-4 animate-spin" />
                                Cancelling...
                              </>
                            ) : (
                              <>
                                <XCircle className="h-4 w-4" />
                                Yes, Cancel
                              </>
                            )}
                          </button>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Reorder Button */}
                {(selectedOrder.status === 'collected' || selectedOrder.status === 'cancelled') && (
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleReorder(selectedOrder.order_id)}
                    disabled={reordering}
                    className="w-full h-14 bg-primary text-white rounded-2xl font-semibold text-lg flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="reorder-btn"
                  >
                    {reordering ? (
                      <>
                        <RefreshCcw className="h-5 w-5 animate-spin" />
                        Adding to Cart...
                      </>
                    ) : (
                      <>
                        <RefreshCcw className="h-5 w-5" />
                        Reorder
                      </>
                    )}
                  </motion.button>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
